import { Spring, spring } from 'svelte/motion';
import { browser } from '$app/env';

type Callback = (val: number) => void;

type Args = {
  countUp?: boolean;
  startedAt?: number;
  durationMs?: number;
  onComplete?: () => void;
};

export default class Timer {
  private subscriptions: Record<symbol, Callback> = {};
  private unsubscribe: () => void;
  private onComplete?: () => void;
  private durationMs?: number;
  private lastResumed: number;
  private startedAt?: number;
  private animationFrame: number;
  private lastTick = 0;
  private state: 'paused' | 'resumed' = 'resumed';
  private countUp?: boolean;

  constructor(args: Args) {
    this.animationFrame = -1;
    this.lastResumed = Date.now();
    this.startedAt = args.startedAt;
    this.countUp = args.countUp;
    this.durationMs = args.durationMs;
    this.onComplete = args.onComplete;
    // initialize timer;
    this.unsubscribe = () => {};
    this.getTimer(args);
  }

  private getTimer(args: Args) {
    this.unsubscribe();
    this.lastResumed = Date.now();
    // the update function sets the latest date
    const next = () => {
      const now = Date.now();
      let seconds: number = 0;
      if (args.countUp) {
        seconds = (now - (this.startedAt ?? 0)) / 1000;
      } else if (args.durationMs) {
        seconds = (args.durationMs - (now - this.lastResumed)) / 1000;
      }
      // TODO consider supporting less frequent intervals
      if (seconds >= 0) {
        this.update(seconds);
        this.animationFrame = requestAnimationFrame(next);
      } else {
        this.update(0);
        cancelAnimationFrame(this.animationFrame);
        this.onComplete?.();
      }
    };

    // force an update to initialize the store with a non-null value
    if (browser) {
      next();
    }
    this.unsubscribe = () => cancelAnimationFrame(this.animationFrame);
  }

  subscribe(callback: Callback) {
    callback(this.lastTick);
    const id = Symbol();
    this.subscriptions[id] = callback;
    return () => {
      delete this.subscriptions[id];
      // clear after last subscription closes
      if (Object.getOwnPropertySymbols(this.subscriptions).length === 0) {
        this.unsubscribe();
      }
    };
  }

  update(val: number) {
    this.lastTick = val;
    for (const id of Object.getOwnPropertySymbols(this.subscriptions)) {
      this.subscriptions[id](val);
    }
  }

  start(args: Args) {
    this.state = 'resumed';
    this.startedAt = args.startedAt;
    this.onComplete = args.onComplete;
    this.getTimer(args);
  }

  pause() {
    if (this.state === 'resumed') {
      this.state = 'paused';
      if (!this.countUp && this.durationMs) {
        this.durationMs =
          this.durationMs - (Date.now() - this.lastResumed) / 1000;
      }
      this.unsubscribe();
    }
  }

  resume() {
    if (this.state === 'paused') {
      this.state = 'resumed';
      this.getTimer({
        countUp: this.countUp,
        durationMs: this.durationMs,
        onComplete: this.onComplete,
      });
    }
  }

  async addTime(
    seconds: number,
    { stiffness = 0.025, damping = 0.2, soft = 0.75 } = {},
  ) {
    this.pause();
    // const s = new Spring<number>(this.lastTick, { stiffness, damping });
    const s = spring<number>(this.lastTick, { stiffness, damping });
    const unsubscribe = s.subscribe((v) => this.update(v));
    if (this.durationMs) {
      this.durationMs = Math.max(this.durationMs, 0) + seconds;
    }
    await s.set(this.lastTick + seconds, { soft });
    unsubscribe();
    this.resume();
  }
}
